import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useFlow } from 'context/FlowContext';
import { summarizeSelectedIngredients } from 'utils/ingredientSummary';
import { getLineUnitPrice, getPremiumSurcharge } from 'utils/cartPricing';
import { groupCartLinesBySignature } from 'utils/cartSignature';
import { memberDisplayName } from 'utils/household';

export default function CartDrawer({ open, onClose }) {
  const {
    cart, cartCount, cartSubtotal, householdMembers,
    addCartLine, setCartLineQuantity, removeFromCart, updateCartLine,
  } = useFlow();

  const memberName = (id) =>
    memberDisplayName(householdMembers.find((m) => m.id === id), 'Guest');

  const groupedLines = useMemo(() => groupCartLinesBySignature(cart), [cart]);

  // Each serving is its own line (and its own menu card), so + adds a copy rather than bumping a count.
  const incrementGroup = (group) => addCartLine(group.lines[0]);

  const decrementGroup = (group) => {
    const last = group.lines[group.lines.length - 1];
    if ((last.quantity || 1) > 1) setCartLineQuantity(last.lineId, last.quantity - 1);
    else removeFromCart(last.lineId);
  };

  if (!open) return null;

  return (
    <div className="sf-cart-drawer" role="dialog" aria-modal="true" aria-label="Shopping cart">
      <button type="button" className="sf-cart-drawer__backdrop" onClick={onClose} aria-label="Close cart" />
      <div className="sf-cart-drawer__panel">
        <header className="sf-cart-drawer__header">
          <h2>
            Cart <span className="sf-cart-drawer__count">({cartCount})</span>
          </h2>
          <button type="button" className="sf-cart-drawer__close" onClick={onClose}>
            ×
          </button>
        </header>

        {cart.length === 0 ? (
          <p className="sf-cart-drawer__empty">Your cart is empty.</p>
        ) : (
          <ul className="sf-cart-drawer__list">
            {groupedLines.map((group) => {
              const line = group.lines[0];
              return (
                <li key={group.key} className="sf-cart-drawer__line">
                  <div className="sf-cart-drawer__line-info">
                    <strong>{line.name}</strong>
                    <span className="sf-cart-drawer__meta">
                      {line.mealType} ·{' '}
                      {householdMembers.length > 1 ? (
                        <>
                          For{' '}
                          <select
                            value={line.memberId}
                            aria-label={`Who is ${line.name} for?`}
                            title="This name goes on the delivery label"
                            onChange={(e) => group.lines.forEach((l) => updateCartLine(l.lineId, { memberId: e.target.value }))}
                            style={{
                              font: 'inherit', color: 'inherit', background: 'transparent',
                              border: '1px solid #d8d0bd', borderRadius: '6px', padding: '1px 4px',
                            }}
                          >
                            {householdMembers.map((m) => (
                              <option key={m.id} value={m.id}>{memberDisplayName(m)}</option>
                            ))}
                          </select>
                        </>
                      ) : (
                        <>For {memberName(line.memberId)}</>
                      )}
                    </span>
                    <div className="sf-cart-drawer__ingredients">
                      {summarizeSelectedIngredients(line).map(({ label, names }) => (
                        <div key={label} className="sf-cart-drawer__ingredient-row">
                          <span className="sf-cart-drawer__ingredient-label">{label}:</span>{' '}
                          {names.join(', ')}
                        </div>
                      ))}
                    </div>
                    <span className="sf-cart-drawer__line-price">
                      ${(getLineUnitPrice(line) * group.quantity).toFixed(2)}
                      {getPremiumSurcharge(line) > 0 && (
                        <span className="sf-cart-drawer__surcharge">
                          {' '}(incl. +${getPremiumSurcharge(line).toFixed(2)} premium protein)
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="sf-cart-drawer__line-actions sf-cart-qty sf-cart-qty--compact">
                    <button
                      type="button"
                      className="sf-cart-qty__btn"
                      aria-label={`Decrease quantity of ${line.name}`}
                      onClick={() => decrementGroup(group)}
                      disabled={group.quantity <= 1}
                    >
                      −
                    </button>
                    <span className="sf-cart-qty__count" aria-live="polite">{group.quantity}</span>
                    <button
                      type="button"
                      className="sf-cart-qty__btn"
                      aria-label={`Increase quantity of ${line.name}`}
                      onClick={() => incrementGroup(group)}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      className="sf-cart-qty__remove"
                      onClick={() => group.lines.forEach((l) => removeFromCart(l.lineId))}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <footer className="sf-cart-drawer__footer">
          <div className="sf-cart-drawer__subtotal">
            <span>Subtotal</span>
            <strong>${cartSubtotal.toFixed(2)}</strong>
          </div>
          {cart.length === 0 ? (
            <button
              type="button"
              className="sf-btn-flow sf-btn-flow--primary sf-btn-flow--block"
              disabled
            >
              Checkout
            </button>
          ) : (
            <Link
              to="/checkout"
              className="sf-btn-flow sf-btn-flow--primary sf-btn-flow--block"
              onClick={onClose}
            >
              Checkout
            </Link>
          )}
          <Link to="/this-weeks-menu" className="sf-btn-flow sf-btn-flow--ghost sf-btn-flow--block" onClick={onClose}>
            Continue shopping
          </Link>
        </footer>
      </div>
    </div>
  );
}
